/**
 * Shared formatting utilities for currency, percentages, numbers, and dates.
 *
 * Replaces inline formatReturn() / formatAum() duplicated across
 * StrategyMetrics.tsx and StrategiesTableView.tsx.
 */

/**
 * Format a number as USD currency: "$1,234.56"
 * Negative values render as "-$500.10".
 */
export function formatCurrency(value: number, decimals = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number as compact currency: "$1.2M", "$450K", "$900"
 */
export function formatCurrencyCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}$${abs.toFixed(0)}`;
}

/**
 * Format a decimal as percentage: 0.042 → "4.20%"
 * Handles both decimal (0.042) and whole number (4.2) inputs.
 * Values with abs < 1 are treated as decimal fractions.
 */
export function formatPercent(value: number, decimals = 2): string {
  const pct = Math.abs(value) < 1 ? value * 100 : value;
  return `${pct.toFixed(decimals)}%`;
}

/**
 * Format a return value with sign prefix: "+4.2%" or "-1.5%"
 * Input is a whole-number percentage (e.g. 4.2 not 0.042).
 */
export function formatReturn(value: number, decimals = 2): string {
  const prefix = value >= 0 ? "+" : "";
  return `${prefix}${value.toFixed(decimals)}%`;
}

/**
 * Format a number with locale separators: "1,234,567"
 */
export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a date string or Date object: "Jan 15, 2026"
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a date as relative time: "3 days ago", "2 hours ago", "just now"
 */
export function formatRelativeDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;

  return formatDate(d);
}

/**
 * Parse a date string in various formats:
 * - ISO: "2024-01-15T00:00:00Z"
 * - YYYY-MM-DD: "2024-01-15"
 * - YYYYMMDD: "20240115"
 * - YYYY-MM: "2024-01" (defaults to 1st of month)
 */
export function parseDate(date: string): Date {
  // YYYYMMDD (8 digits, no separators)
  if (/^\d{8}$/.test(date)) {
    const year = parseInt(date.slice(0, 4), 10);
    const month = parseInt(date.slice(4, 6), 10) - 1;
    const day = parseInt(date.slice(6, 8), 10);
    return new Date(year, month, day);
  }

  // YYYY-MM (year-month only)
  if (/^\d{4}-\d{2}$/.test(date)) {
    const [year, month] = date.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }

  // YYYY-MM-DD (parse as local, not UTC, to avoid day-shift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Fall through to native parser for ISO datetimes
  return new Date(date);
}
